import { useState } from "react";
import { TrashIcon } from "lucide-react";

import BreadCrumb from "../../../Components/BreadCrumb";
import TableListing from "../../../Components/TableListing";
import { Button } from "../../../Components/Buttons";
import { ToastMessage, getSelectedIds } from "../../../Components/Utils";
import { handleError } from "../../../stores/components/handleError";
import {
  useFetchSubscribersQuery,
  useDeleteSubscriberMutation,
  useBulkDeleteSubscribersMutation,
} from "../../../stores/api/subscribers.api";
import payloadHelper from "../../../stores/api/helpers/payload.helper";

const EMPTY_PAGINATION = {
  data: [],
  current_page: 1,
  total_pages: 1,
  total_items: 0,
  current_items: 0,
  limit: 10,
};

const HEADERS = [
  { key: "id", label: "#" },
  { key: "email", label: "Email" },
  { key: "createdAt", label: "Subscribed On" },
];

const SubscribersList = () => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const queryParams = payloadHelper.forList({
    page,
    limit,
    search,
    sortBy: sortConfig.key,
    sortOrder: sortConfig.direction,
  });

  const { data: subscribers = EMPTY_PAGINATION, isFetching } = useFetchSubscribersQuery(queryParams);
  const [deleteSubscriber] = useDeleteSubscriberMutation();
  const [bulkDelete] = useBulkDeleteSubscribersMutation();

  const resetPage = () => setPage(1);
  const rows = subscribers.data.map((r) => ({
    ...r,
    createdAt: new Date(r.createdAt).toLocaleString("en-PK", {
      dateStyle: "medium",
      timeStyle: "short",
    }),
  }));
  const handleSingle = (row) => {
    ToastMessage({
      type: "warning",
      title: "Delete Subscriber",
      message: `Delete "${row.email}"?`,
      toastType: "confirm",
      onYes: async () => {
        try {
          await deleteSubscriber(row.id).unwrap();
          ToastMessage({ type: "success", title: "Done!", message: "Subscriber deleted." });
        } catch (err) {
          handleError(err, "Failed.");
        }
      },
    });
  };

  const handleBulk = (selectedItems) => {
    const ids = getSelectedIds(selectedItems);
    ToastMessage({
      type: "warning",
      title: "Bulk Delete",
      message: `Delete ${ids.length} subscribers?`,
      toastType: "confirm",
      onYes: async () => {
        try {
          await bulkDelete(payloadHelper.forBulk(ids)).unwrap();
          ToastMessage({ type: "success", title: "Done!", message: `${ids.length} subscribers deleted.` });
        } catch (err) {
          handleError(err, "Failed bulk delete.");
        }
      },
    });
  };

  return (
    <>
      <BreadCrumb title="Subscribers" />
      <TableListing
        headers={HEADERS}
        paginationData={{...subscribers , data:rows}}
        loading={isFetching}
        handleBulk={handleBulk}
        search={search}
        setSearch={setSearch}
        trashed={false}
        setTrashed={() => {}}
        onPageChange={setPage}
        onRowsPerPageChange={(value) => {
          setLimit(value);
          resetPage();
        }}
        onSearch={(value) => {
          setSearch(value);
          resetPage();
        }}
        onSortChange={(sort) => {
          setSortConfig(sort);
          resetPage();
        }}
        actions={(row) => (
          <Button
            onClick={() => handleSingle(row)}
            title="Delete"
            icon={TrashIcon}
            className="text-red-600 hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-500/10"
          />
        )}
      />
    </>
  );
};

export default SubscribersList;
